
import React, { useState, useEffect } from 'react';
import { sectionLabel } from '../../constants/sections';
import { SiteConfig, PageSection, CompanySettings } from '../../types';
import { HeroSection } from './sections/HeroSection';
import { FeaturesSection } from './sections/FeaturesSection';
import { ContentSection } from './sections/ContentSection';
import { ProductShowcase } from './sections/ProductShowcase';
import { LandingProductGrid } from './sections/LandingProductGrid';
import { ShopContentSection } from '../store/sections/ShopContentSection';
import { SiteFooter } from './SiteFooter';
import { LogIn, Edit2, Loader2, LayoutDashboard } from 'lucide-react';
import { BrandLogo } from '../ui/BrandLogo';

interface PageBuilderProps {
    config: SiteConfig;
    companySettings?: CompanySettings; // Added prop
    onAction: (action: string) => void;
    onSectionClick?: (id: string) => void;
    activeSectionId?: string | null;
    scrollContainerId?: string; 
    isAuthenticated?: boolean;
    /** Besökaren har klickat men inloggningskollen har inte svarat än. */
    isAuthPending?: boolean;
}

export const PageBuilder: React.FC<PageBuilderProps> = ({ 
    config, 
    companySettings,
    onAction, 
    onSectionClick, 
    activeSectionId, 
    scrollContainerId,
    isAuthenticated,
    isAuthPending
}) => {
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const target = scrollContainerId ? document.getElementById(scrollContainerId) : window;
        const handleScroll = () => {
            const y = scrollContainerId && target instanceof HTMLElement 
                ? target.scrollTop 
                : window.scrollY;
            setIsScrolled(y > 20);
        };
        target?.addEventListener('scroll', handleScroll);
        // Initial check
        handleScroll();
        return () => target?.removeEventListener('scroll', handleScroll);
    }, [scrollContainerId]);

    // SEO: Set Title (Only if not in preview mode)
    useEffect(() => {
        if (!scrollContainerId) {
            document.title = "Sälj Kaffe & Tjäna Pengar till Klasskassan | CoffeeNova";
        }
    }, [scrollContainerId]);
    
    const renderSectionContent = (section: PageSection) => {
        switch(section.type) {
            case 'HERO': return <HeroSection key={section.id} section={section} onAction={onAction} theme={config.theme} />;
            case 'FEATURES': return <FeaturesSection key={section.id} section={section} />;
            case 'CONTENT': return <ContentSection key={section.id} section={section} />;
            case 'PRODUCTS': return <ProductShowcase key={section.id} section={section} theme={config.theme} />;
            case 'PRODUCT_GRID': return <LandingProductGrid key={section.id} section={section} theme={config.theme} />;
            case 'REVIEWS':
            case 'MEDIA': return <ShopContentSection key={section.id} section={section} editing={!!onSectionClick} />;
            default: return null;
        }
    };

    // If inside preview (scrollContainerId exists), make it sticky but pull content up
    // so it overlays the hero properly (simulating fixed)
    const navPositionClass = scrollContainerId ? 'sticky top-0 mb-[-72px]' : 'fixed top-0';

    return (
        <div className="bg-background min-h-screen font-sans flex flex-col">
            
            {/* --- CMS NAVBAR (Fixed & Adaptive) --- */}
            <nav 
                className={`${navPositionClass} left-0 right-0 z-50 transition-all duration-500`}
            >
                {/* Background Layers */}
                <div 
                    className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${isScrolled ? 'opacity-100' : 'opacity-0'}`}
                    style={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                        backdropFilter: 'blur(10px)',
                        borderBottom: '1px solid rgba(0,0,0,0.05)'
                    }}
                />
                <div 
                    className={`absolute inset-0 bg-gradient-to-b from-black/60 to-transparent transition-opacity duration-700 ease-in-out pointer-events-none ${isScrolled ? 'opacity-0' : 'opacity-100'}`} 
                />

                <div className="relative flex justify-between items-center px-4 md:px-6 py-4">
                    {/* Logo Area with Smooth Transition */}
                    <div 
                        className="relative h-8 w-40 flex items-center cursor-pointer pointer-events-auto origin-left" 
                        onClick={() => {
                            if (scrollContainerId) {
                                document.getElementById(scrollContainerId)?.scrollTo({top: 0, behavior: 'smooth'});
                            } else {
                                window.scrollTo({top: 0, behavior: 'smooth'});
                            }
                        }}
                    >
                        <div className={`absolute left-0 top-1/2 -translate-y-1/2 transition-all duration-500 ${isScrolled ? 'opacity-0 scale-95 -translate-x-2' : 'opacity-100 scale-100 translate-x-0'}`}>
                            <BrandLogo variant="light" layout="minimal" theme={config.theme} className="drop-shadow-md" fadeIn />
                        </div>
                        <div className={`absolute left-0 top-1/2 -translate-y-1/2 transition-all duration-500 ${isScrolled ? 'opacity-100 scale-90 translate-x-0' : 'opacity-0 scale-95 translate-x-2'}`}>
                            <BrandLogo variant="dark" layout="minimal" theme={config.theme} fadeIn />
                        </div>
                    </div>
                    
                    {/* Actions Area */}
                    <div className="flex gap-4 pointer-events-auto">
                        <button 
                            onClick={() => onAction('LOGIN')}
                            disabled={isAuthPending}
                            className={`h-9 px-5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all duration-500 flex items-center justify-center gap-2 hover:scale-105 active:scale-95 ${
                                isScrolled
                                ? 'bg-stone-900 text-white hover:bg-stone-800 shadow-sm'
                                : 'bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white shadow-lg'
                            }`}
                        >
                            {isAuthPending ? (
                                <Loader2 size={12} className="animate-spin" />
                            ) : isAuthenticated ? (
                                <><LayoutDashboard size={12} /> <span>Min Panel</span></>
                            ) : (
                                <><LogIn size={12} /> <span>Logga in</span></>
                            )}
                        </button>
                    </div>
                </div>
            </nav>

            {/* --- RENDER SECTIONS --- */}
            <div className="flex flex-col flex-1">
                {config.sections.map((section) => {
                    const isActive = section.id === activeSectionId;
                    const isEditable = !!onSectionClick;

                    return (
                        <div 
                            key={section.id}
                            id={section.id} 
                            onClick={(e) => {
                                if (isEditable && onSectionClick) {
                                    e.stopPropagation();
                                    onSectionClick(section.id);
                                }
                            }}
                            className={`relative transition-all duration-200 ${
                                isEditable 
                                ? 'cursor-pointer hover:ring-2 hover:ring-coffee-400 hover:ring-inset hover:z-10' 
                                : ''
                            } ${
                                isActive 
                                ? 'ring-4 ring-coffee-600 ring-inset z-20 shadow-2xl' 
                                : ''
                            }`}
                        >
                            {isEditable && (
                                <>
                                    <div className={`absolute top-4 left-1/2 -translate-x-1/2 bg-coffee-600 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-lg flex items-center gap-2 transition-opacity z-50 pointer-events-none ${
                                        isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                    }`}>
                                        <Edit2 size={10} /> Redigerar: {sectionLabel(section.type)}
                                    </div>
                                    {/* Overlay to capture clicks in edit mode */}
                                    <div className="absolute inset-0 z-40 cursor-pointer" />
                                </>
                            )}
                            
                            <div className={isEditable ? 'pointer-events-none' : ''}>
                                {renderSectionContent(section)}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Pass company settings to Footer */}
            <SiteFooter 
                config={config} 
                company={companySettings || { name: 'CoffeeNova', orgNumber: '', email: '', phone: '', street: '', zip: '', city: '' }}
                onAction={onAction} 
            />
        </div>
    );
};
